import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const SHOTS = {
  week: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/rwbeq6yx_IMG_1225.webp",
  day: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/x0mcesa2_IMG_1226.webp",
  coach: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/3xf0uo4t_IMG_1227.webp",
};

const CHAPTERS = [
  {
    num: "01",
    title: "The roster is the input.",
    body: "Upload your roster and ROSTENO reads every shift, transition and commute — day shifts, afternoons, nights, and the swings between them.",
    image: SHOTS.week,
    imageAlt: "ROSTENO My Week screen showing the roster week view",
    caption: "Pictured — My Week",
  },
  {
    num: "02",
    title: "Life planned around work.",
    body: "Sleep targets, training windows, meals and recovery, mapped to each shift — so a 04:00 start doesn't cost you your health, and a night shift doesn't cost you your family.",
    image: SHOTS.day,
    imageAlt: "ROSTENO My Day screen with wind-down, sleep, wake and breakfast blocks",
    caption: "Pictured — My Day",
  },
  {
    num: "03",
    title: "A coach that knows your roster.",
    body: "Ask anything — “Should I train before my night shift?” — and get an answer built on your actual week, not generic advice copied from a 9–5 blog.",
    image: SHOTS.coach,
    imageAlt: "ROSTENO Coach screen with shift-aware question prompts",
    caption: "Pictured — ROSTENO Coach",
  },
  {
    num: "04",
    title: "Roster changed? Plan rebuilt.",
    body: "When work throws a curveball, ROSTENO rebuilds your sleep, food, training and recovery around it automatically. Work changes. Life shouldn't.",
    image: null,
  },
];

function ParallaxShot({ src, alt, testId }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div
      ref={ref}
      className="overflow-hidden rounded-[2rem] border border-white/15 bg-[#0b1526] h-[50vh] md:h-[65vh]"
    >
      <motion.img
        data-testid={testId}
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-full object-cover object-top scale-110"
      />
    </div>
  );
}

function Chapter({ chapter, index }) {
  const reversed = index % 2 === 1;
  const text = (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className={reversed ? "md:order-2" : ""}
    >
      <span
        data-testid={`chapter-${chapter.num}-number`}
        className="font-mono-accent text-sm tracking-[0.3em] text-[#4ADE80]"
      >
        {chapter.num}
      </span>
      <h2
        data-testid={`chapter-${chapter.num}-title`}
        className="font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-6"
      >
        {chapter.title}
      </h2>
      <p
        data-testid={`chapter-${chapter.num}-body`}
        className="mt-8 text-sm sm:text-base leading-relaxed text-[#8B99AE] max-w-md"
      >
        {chapter.body}
      </p>
    </motion.div>
  );

  if (!chapter.image) {
    return (
      <div className="border-t border-white/10 px-6 md:px-12 py-32 md:py-48">
        <div className="max-w-3xl">{text}</div>
      </div>
    );
  }

  return (
    <div className="border-t border-white/10 px-6 md:px-12 py-32 md:py-48">
      <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        {text}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className={reversed ? "md:order-1" : ""}
        >
          <ParallaxShot
            src={chapter.image}
            alt={chapter.imageAlt}
            testId={`chapter-${chapter.num}-image`}
          />
          <p className="mt-4 font-mono-accent text-xs uppercase tracking-[0.25em] text-[#8B99AE]">
            {chapter.caption}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section data-testid="manifesto-section">
      {CHAPTERS.map((chapter, i) => (
        <Chapter key={chapter.num} chapter={chapter} index={i} />
      ))}
    </section>
  );
}
